<template>
    <DashboardLayout ref="dashboardLayout">
        <Breadcrumb class="breadcrumb" :model="items" />

        <div class="card flex flex-column justify-center pt-4 px-4">

            <div style="color: #222; font-weight: 500; margin: 16px 0 8px 0;">
                    <span style="font-size: 1.2em; margin-right: 6px;">⚠️</span>
                    <b>Warning:</b> Class requests are automatically rejected if not accepted within 2 days.
            </div>

            <div v-if="classes.length" class="grid-container md:p-4">
                <div v-for="(classItem, index) in classes" :key="index">
                    <Card class="classCard p-4 h-full relative">
                        <template #content>
                            <div class="flex flex-column h-full">
                                <div v-if="classItem.isReschedule"
                                    class="absolute top-0 left-0 m-2 py-1 px-2 text-xs font-semibold bg-gray-50 text-gray-400 border-1 border-gray-300 border-round-md">
                                    Reschedule
                                </div>
                                <div class="absolute top-0 right-0 m-2 pr-1">
                                    <ChatButton :otherUserId="classItem.tutorId" :otherUserName="classItem.tutor"
                                        @open-chat="handleOpenChat" />
                                </div>

                                <p class="text-center align-self-center Paragraph-Medium mb-3">{{ classItem.className }}
                                </p>
                                <div class="flex justify-content-center gap-3 text-sm">
                                    <div class="flex flex-column gap-2">
                                        <span class="Caption font-semibold">Tutor:</span>
                                        <span class="Caption font-semibold">Class date:</span>
                                        <span class="Caption font-semibold">Class time:</span>
                                        <span class="Caption font-semibold">Class type:</span>
                                        <span class="Caption font-semibold">Language:</span>
                                    </div>
                                    <div class="flex flex-column gap-2" style="min-width: 0; overflow-wrap: break-word; word-break: break-word;">
                                        <span class="Caption">{{ classItem.tutor }}</span>
                                        <span class="Caption">{{ classItem.date }}</span>
                                        <span class="Caption">{{ classItem.time }}</span>
                                        <span class="Caption">{{ classItem.type }}</span>
                                        <span class="Caption">{{ classItem.language }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #footer>
                            <div class="flex justify-content-center gap-2 mt-3">
                                <Button label="Accept" class="orange-button-small"
                                    @click="openAcceptDialog(classItem)" />
                                <Button label="Reject" class="p-button-outlined p-button-danger"
                                    @click="handleAction(classItem, 'reject')" />
                            </div>
                            <div class="mt-3 p-2 bg-blue-50 border-l-4 border-blue-500 rounded">
                                <p class="text-xs">This request will be automatically rejected if not accepted before
                                    <span class="font-bold">{{ classItem.date }} at {{ classItem.time.split(' To ')[0] }}</span>.
                                </p>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>

            <div v-else class="flex align-item-center justify-content-center h-full">
                <div class="flex flex-column lg:col-3 md:col-4">
                    <span class="Caption text-gray-700 text-center">
                        You don’t have any class confirmation requests yet, please request a class from your favorite
                        tutor
                    </span>
                </div>
            </div>
        </div>

        <!-- Accept Confirmation Dialog -->
        <Dialog v-model:visible="acceptDialogVisible" modal :closable="true" :draggable="false"
            style="max-width: 500px; width: 95vw;" class="p-fluid">

            <template #header>
                <div class="flex align-items-center gap-2">
                    <span class="text-900 font-bold" style="font-size: 1.1rem;">
                        {{ pendingAcceptItem?.isReschedule ? 'Confirm Reschedule' : 'Confirm Acceptance' }}
                    </span>
                    <span v-if="pendingAcceptItem?.isReschedule" class="reschedule-badge">Reschedule</span>
                </div>
            </template>

            <div v-if="pendingAcceptItem">

                <!-- Class title -->
                <div class="mb-4 text-center">
                    <h3 class="m-0 text-900 font-bold text-lg mb-1">{{ pendingAcceptItem.className }}</h3>
                    <p class="m-0 text-500 text-sm">Please review the details below before confirming.</p>
                </div>

                <!-- Tutor / Type / Language -->
                <div class="surface-50 border-1 border-200 border-round-lg p-3 mb-4">
                    <div class="flex flex-column gap-2">
                        <div class="flex justify-content-between align-items-center pb-2 border-bottom-1 border-200">
                            <span class="text-600 text-sm font-medium">Tutor</span>
                            <span class="text-900 text-sm font-bold">{{ pendingAcceptItem.tutor }}</span>
                        </div>
                        <div class="flex justify-content-between align-items-center pb-2 border-bottom-1 border-200">
                            <span class="text-600 text-sm font-medium">Class Type</span>
                            <span class="text-900 text-sm font-bold">{{ pendingAcceptItem.type }}</span>
                        </div>
                        <div class="flex justify-content-between align-items-center">
                            <span class="text-600 text-sm font-medium">Language</span>
                            <span class="text-900 text-sm font-bold">{{ pendingAcceptItem.language }}</span>
                        </div>
                    </div>
                </div>

                <!-- Section divider -->
                <div class="flex align-items-center gap-2 mb-3">
                    <span class="text-500 text-xs font-bold uppercase"
                        style="letter-spacing: 0.05em; white-space: nowrap;">
                        {{ pendingAcceptItem.isReschedule ? 'Schedule & Fee Summary'
                            : (isRecurring ? 'Scheduled Sessions' : 'Session & Fee Summary') }}
                        <span v-if="isRecurring && !pendingAcceptItem.isReschedule"
                            class="text-400 font-normal" style="text-transform: none; letter-spacing: 0;">
                            ({{ pendingAcceptItem.instancesPreview.length }})
                        </span>
                    </span>
                    <div class="flex-1 border-top-1 border-200"></div>
                </div>

                <!-- ── RESCHEDULE ── -->
                <template v-if="pendingAcceptItem.isReschedule">
                    <div class="grid mb-3">
                        <div class="col-5">
                            <div class="surface-100 border-round-lg p-3 h-full text-center">
                                <div class="text-400 text-xs font-bold uppercase mb-2"
                                    style="letter-spacing: 0.04em;">Current</div>
                                <div class="text-700 text-sm font-semibold mb-1">
                                    {{ formatToDisplayDate(pendingAcceptItem.currentSchedule.date) }}
                                </div>
                                <div class="text-500 text-xs">
                                    {{ pendingAcceptItem.currentSchedule.start }} &ndash;
                                    {{ pendingAcceptItem.currentSchedule.end }}
                                </div>
                            </div>
                        </div>
                        <div class="col-2 flex align-items-center justify-content-center">
                            <i class="pi pi-arrow-right text-400 text-xl"></i>
                        </div>
                        <div class="col-5">
                            <div class="border-round-lg p-3 h-full text-center"
                                style="background: #fff8f0; border: 1px solid #fbd38d;">
                                <div class="text-xs font-bold uppercase mb-2"
                                    style="color: #d97706; letter-spacing: 0.04em;">New</div>
                                <div class="text-sm font-semibold mb-1 text-900">
                                    {{ formatToDisplayDate(pendingAcceptItem.newSchedule.date) }}
                                </div>
                                <div class="text-xs text-600">
                                    {{ pendingAcceptItem.newSchedule.start }} &ndash;
                                    {{ pendingAcceptItem.newSchedule.end }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="pendingAcceptItem.sessionFee !== null"
                        class="surface-50 border-1 border-200 border-round-lg p-3 flex justify-content-between align-items-center mb-3">
                        <span class="text-600 text-sm font-bold uppercase"
                            style="letter-spacing: 0.04em;">Session Fee</span>
                        <span class="text-900 font-bold text-lg">LKR {{ formatFee(pendingAcceptItem.sessionFee) }}</span>
                    </div>
                </template>

                <!-- ── NEW SINGLE CLASS ── -->
                <template v-else-if="!isRecurring">
                    <div class="surface-0 border-1 border-200 border-round-lg p-3 mb-3">
                        <div class="flex flex-column gap-3">
                            <div class="flex justify-content-between align-items-center">
                                <span class="text-600 text-sm font-medium">Date</span>
                                <span class="text-900 text-sm font-semibold">{{ pendingAcceptItem.date }}</span>
                            </div>
                            <div class="flex justify-content-between align-items-center">
                                <span class="text-600 text-sm font-medium">Time</span>
                                <span class="text-900 text-sm font-semibold">{{ pendingAcceptItem.time }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="pendingAcceptItem.sessionFee !== null" class="mb-3">
                        <div class="surface-100 border-1 border-200 border-round-lg p-3 flex justify-content-between align-items-center">
                            <span class="text-600 text-sm font-bold uppercase"
                                style="letter-spacing: 0.04em;">Session Fee</span>
                            <span class="text-900 font-bold text-lg">LKR
                                {{ formatFee(pendingAcceptItem.sessionFee) }}</span>
                        </div>
                    </div>
                </template>

                <!-- ── NEW RECURRING CLASS ── -->
                <template v-else>
                    <div class="instances-table mb-3">
                        <div class="instance-row instance-header">
                            <span>Date</span>
                            <span>Time</span>
                            <span class="col-right">Fee (LKR)</span>
                        </div>
                        <div v-for="(inst, i) in pendingAcceptItem.instancesPreview" :key="i"
                            class="instance-row">
                            <span class="text-700">{{ formatToDisplayDate(inst.date) }}</span>
                            <span class="text-700">{{ inst.start }} &ndash; {{ inst.end }}</span>
                            <span class="col-right font-medium">{{ formatFee(inst.fee) }}</span>
                        </div>
                        <div class="instance-row instance-total">
                            <span class="col-span-2 text-sm text-700 font-bold uppercase"
                                style="letter-spacing: 0.04em;">Total Amount</span>
                            <span class="col-right font-bold text-xl">LKR
                                {{ formatFee(totalFee) }}</span>
                        </div>
                    </div>
                </template>

                <!-- Disclaimers card -->
                <div class="surface-50 border-1 border-200 border-round-lg p-3 shadow-1">
                    <div class="flex align-items-start gap-3 mb-3 pb-3 border-bottom-1 border-200">
                        <i class="pi pi-wallet text-red-500 text-xl mt-1"></i>
                        <div>
                            <div class="font-bold text-900 text-sm mb-1">Auto-Charge Authorization</div>
                            <div class="text-600 text-xs line-height-3">
                                The applicable fee is automatically deducted from your wallet
                                <b>before each class begins</b>. Please ensure sufficient balance
                                prior to each session.
                            </div>
                        </div>
                    </div>
                    <div class="flex align-items-start gap-3">
                        <i class="pi pi-shield text-indigo-500 text-xl mt-1"></i>
                        <div>
                            <div class="font-bold text-900 text-sm mb-1">Terms of Acceptance</div>
                            <div class="text-600 text-xs line-height-3">
                                <template v-if="pendingAcceptItem.isReschedule">
                                    By clicking <b>Confirm &amp; Accept</b>, you agree to the new schedule
                                    and authorize iLearned to process the fee automatically. Fees are
                                    non-refundable once a class has begun.
                                </template>
                                <template v-else>
                                    By clicking <b>Confirm &amp; Accept</b>, you agree to the scheduled
                                    session(s) and authorize iLearned to process the applicable fee
                                    automatically. Fees are non-refundable once a class has begun.
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <template #footer>
                <div class="flex w-full gap-2 mt-3">
                    <Button label="Cancel" class="p-button-text p-button-secondary font-bold footer-action-btn"
                        @click="acceptDialogVisible = false" />
                    <Button label="Confirm &amp; Accept" class="orange-button-small font-bold footer-action-btn"
                        @click="confirmAccept" />
                </div>
            </template>
        </Dialog>

        <!-- No Saved Card Dialog -->
        <Dialog v-model:visible="noCardDialogVisible" modal header="Add a Payment Card"
            :style="{ width: '26rem' }" :draggable="false">
            <div class="flex flex-column gap-3 py-3">
                <div class="flex align-items-start gap-3">
                    <i class="pi pi-credit-card mt-1" style="font-size: 1.25rem; color: var(--orange-500);" />
                    <p class="m-0 text-sm text-color-secondary">
                        You don't have a saved payment card yet, so this class can't be accepted.
                        Please go to <b>Settings &rarr; Payment</b> and add a card before accepting the class.
                    </p>
                </div>
            </div>
            <template #footer>
                <Button label="Not Now" icon="pi pi-times"
                    class="p-button-text p-button-sm p-button-secondary" @click="noCardDialogVisible = false" />
                <Button label="Go to Settings" icon="pi pi-credit-card"
                    class="orange-button-small p-button-sm" @click="goToAddCard" autofocus />
            </template>
        </Dialog>
    </DashboardLayout>
</template>

<script setup>
import { onMounted, ref, reactive, computed } from "vue";
import { useRouter } from 'vue-router';
import Breadcrumb from 'primevue/breadcrumb';
import Card from "primevue/card";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import DashboardLayout from "@/layout/DashboardLayout.vue";
import * as api from '@/api/student';
import payments from "@/api/payments";
import { notifyContrast, notifyError, notifySuccess } from "@/utils/notification";
import { capitalize, formatToDisplayDate, cleanupLables } from "@/utils/cleanups";
import ChatButton from "@/components/ChatButton.vue";

const router = useRouter();

// --- CONSTANTS & STATE ---
const items = reactive([
    { label: 'Dashboard', to: '/' },
    { label: 'Class Confirmations' },
]);
const classes = ref([]);
const acceptDialogVisible = ref(false);
const pendingAcceptItem = ref(null);
const noCardDialogVisible = ref(false);
const cardChecking = ref(false);

// --- DATA FETCHING & PROCESSING ---
const fetchToBeConfirmed = async () => {
    try {
        const res = await api.fetchToBeConfirmed();
        const rawData = res.data.data;

        classes.value = rawData.map(classItem => {
            return {
                id: classItem._id,
                tutorId: classItem.tutorId,
                className: `${cleanupLables(classItem.subject)} Class`,
                tutor: capitalize(classItem.tutor),
                date: formatToDisplayDate(classItem.date),
                time: `${classItem.start} To ${classItem.end}`,
                type: capitalize(classItem.classType),
                language: capitalize(classItem.language),
                isReschedule: classItem.isReschedule || false,
                rescheduleId: classItem.rescheduleId || null,
                instancesPreview: classItem.instancesPreview || [],
                sessionFee: classItem.sessionFee ?? null,
                currentSchedule: classItem.currentSchedule || null,
                newSchedule: classItem.newSchedule || null,
            };
        });

    } catch (error) {
        console.error("Failed to fetch class confirmations:", error);
        notifyError("Error", "Failed to load class confirmations. Please try again later.");
        classes.value = [];
    }
};

// --- ACCEPT DIALOG ---
const isRecurring = computed(() => (pendingAcceptItem.value?.instancesPreview?.length ?? 0) > 0);

const totalFee = computed(() =>
    pendingAcceptItem.value?.instancesPreview?.reduce((sum, i) => sum + (i.fee || 0), 0) ?? 0
);

const formatFee = (amount) =>
    new Intl.NumberFormat('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const openAcceptDialog = async (classItem) => {
    if (cardChecking.value) return;
    cardChecking.value = true;
    try {
        const res = await payments.getMyToken();
        const hasCard = (res?.data?.data ?? {}).hasToken === true;
        if (!hasCard) {
            noCardDialogVisible.value = true;
            return;
        }
        pendingAcceptItem.value = classItem;
        acceptDialogVisible.value = true;
    } catch (error) {
        console.error("Failed to verify saved payment card:", error);
        notifyError("Error", "Could not verify your payment card. Please try again.");
    } finally {
        cardChecking.value = false;
    }
};

const goToAddCard = () => {
    noCardDialogVisible.value = false;
    router.push({ path: '/studentSettings', query: { tab: 'payment' } });
};

const confirmAccept = () => {
    acceptDialogVisible.value = false;
    handleAction(pendingAcceptItem.value, 'accept');
    pendingAcceptItem.value = null;
};

// --- ACTION HANDLERS ---
const handleAction = async (item, action) => {
    notifyContrast("Hold On!", "Processing your request...");
    try {
        if (item.isReschedule) {
            if (action === 'accept') {
                await api.acceptRescheduleRequest(item.rescheduleId);
                notifySuccess("Success", "Reschedule request accepted successfully.");
            } else {
                await api.rejectRescheduleRequest(item.rescheduleId);
                notifySuccess("Success", "Reschedule request rejected successfully.");
            }
        } else {
            if (action === 'accept') {
                await api.acceptSchedule(item.id);
                notifySuccess("Success", "Class accepted successfully.");
            } else {
                await api.rejectSchedule(item.id);
                notifySuccess("Success", "Class rejected successfully.");
            }
        }
    } catch (error) {
        notifyError("Error", `Failed to ${action} the class. Please try again.`);
    }
    fetchToBeConfirmed();
};

// --- LIFECYCLE HOOKS ---
onMounted(() => {
    fetchToBeConfirmed();
});

const dashboardLayout = ref(null);

const handleOpenChat = (channel) => {
    if (dashboardLayout.value) {
        if (channel && typeof dashboardLayout.value.openSelectedChat === 'function') {
            dashboardLayout.value.openSelectedChat(channel);
        } else if (typeof dashboardLayout.value.openChatList === 'function') {
            dashboardLayout.value.openChatList();
        }
    }
}
</script>

<style scoped>
.grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 2rem;
}

.classCard {
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
    height: 100%;
}

.classCard :deep(.p-card-body) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.classCard :deep(.p-card-content) {
    flex-grow: 1;
    padding: 0;
}

.classCard :deep(.p-card-footer) {
    margin-top: auto;
    padding-top: 0;
}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 32px;
    font-size: 14px;
}

.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

@media (max-width: 767px) {
    .breadcrumb {
        font-size: 12px;
    }
}

/* ── Class Confirmation Dialog — Student ── */

.reschedule-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    background: #FEE2E2;
    color: #ec0e37;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
}

/* Recurring sessions table */
.instances-table {
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    overflow: hidden;
}

.instance-row {
    display: grid;
    grid-template-columns: 1fr 1.5fr auto;
    padding: 8px 12px;
    gap: 8px;
    align-items: center;
    color: #111827;
    font-size: 13px;
}

.instance-row + .instance-row {
    border-top: 1px solid #F3F4F6;
}

.instance-header {
    background: #F9FAFB;
    font-weight: 600;
    color: #6B7280;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.instance-row:not(.instance-header):not(.instance-total):nth-child(even) {
    background: #FAFAFA;
}

.instance-total {
    border-top: 1px solid #E5E7EB !important;
    background: #F3F4F6;
    font-weight: 600;
}

.col-right {
    text-align: right;
    white-space: nowrap;
}

.col-span-2 {
    grid-column: span 2;
}

.footer-action-btn {
    flex: 1;
    min-width: 140px;
    height: 2.5rem !important;
    justify-content: center;
}
</style>